// client/src/components/clientes/HistoricoAgendamentos.js (VERSÃO CORRIGIDA)
import React, { useState, useMemo } from 'react';
// 1. Importa os hooks necessários e remove o Context
import { useHistoricoAgendamentos, useResumoAgendamentos } from '../../hooks/useClientes';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import { Loader2, CalendarX2, UserCheck, ClipboardList, TrendingUp, Calendar, Hash, Receipt } from 'lucide-react';
import Pagination from '../common/Pagination';

// Subcomponentes (mantidos como estão)
const getStatusClass = (status) => {
    const statusNormalized = status?.toLowerCase().replace(/\s+/g, '-');
    switch (statusNormalized) {
        case 'confirmado': case 'atendido': return 'status-green';
        case 'agendado': return 'status-blue';
        case 'pendente': case 'não-atendido': return 'status-yellow';
        case 'cancelado': case 'noshow': return 'status-red';
        default: return 'status-gray';
    }
};

const getBadgeClass = (status) => {
    const statusNormalized = status?.toLowerCase().replace(/\s+/g, '-');
    switch (statusNormalized) {
        case 'confirmado': case 'atendido': return 'badge-green';
        case 'agendado': return 'badge-blue';
        case 'pendente': case 'não-atendido': return 'badge-yellow';
        case 'cancelado': case 'noshow': return 'badge-red';
        default: return 'badge-gray';
    }
};

// ✅ 'NoShow' é o valor salvo no banco (ver models/Agendamento.js) — sem
// rótulo em português aqui, o badge mostraria "NoShow" cru.
const getStatusLabel = (status) => status === 'NoShow' ? 'Não Compareceu' : status;

const ResumoAgendamentos = ({ resumo }) => {
    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 'N/A';

    const resumoMetrics = useMemo(() => ({
        totalAgendamentos: resumo?.totalAgendamentos || 0,
        totalGasto: formatCurrency(resumo?.totalGasto || 0, currency, locale),
        frequenciaMedia: resumo?.frequenciaMedia ? `${Math.round(resumo.frequenciaMedia)} dias` : 'N/A',
        ultimoAgendamento: formatDate(resumo?.ultimoAgendamento),
    }), [resumo, currency, locale]);

    return (
        <div className="resumo-agendamentos-grid">
            <div className="resumo-metric-card"><Hash className="metric-icon" /><div><div className="metric-value">{resumoMetrics.totalAgendamentos}</div><div className="metric-label">Agendamentos</div></div></div>
            <div className="resumo-metric-card"><Receipt className="metric-icon" /><div><div className="metric-value">{resumoMetrics.totalGasto}</div><div className="metric-label">Total Gasto</div></div></div>
            <div className="resumo-metric-card"><TrendingUp className="metric-icon" /><div><div className="metric-value">{resumoMetrics.frequenciaMedia}</div><div className="metric-label">Frequência Média</div></div></div>
            <div className="resumo-metric-card"><Calendar className="metric-icon" /><div><div className="metric-value">{resumoMetrics.ultimoAgendamento}</div><div className="metric-label">Último Atendimento</div></div></div>
        </div>
    );
};

const AgendamentoCard = ({ agendamento }) => {
    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const { dataInicio, status, itens, profissional, financeiro } = agendamento;
    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric'})} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    };
    const servicosNomes = itens?.map(item => item.item?.nome || 'Serviço Removido').join(' + ') || 'Serviço não especificado';
    
    return (
        <li className={`agenda-list-item ${getStatusClass(status)}`}>
            <div className="item-time" style={{ fontSize: '0.9rem' }}>{formatDateTime(dataInicio)}</div>
            <div className="item-details">
                <h4 className="item-title"><UserCheck size={14} className="icon" />{profissional?.name || 'Não informado'}</h4>
                <p className="item-subtext"><ClipboardList size={14} className="icon" />{servicosNomes}</p>
            </div>
            <div className="item-meta">
                <div className="item-price">{formatCurrency(financeiro?.total || 0, currency, locale)}</div>
                <div className="item-status">
                    <span className={`badge ${getBadgeClass(status)}`}>{getStatusLabel(status)}</span>
                </div>
            </div>
        </li>
    );
};

// Componente Principal Refatorado
const HistoricoAgendamentos = ({ clienteId }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const agendamentosPerPage = 5;

    // 2. Chama os hooks para buscar os dados.
    const { data: resumoAgendamentos, isLoading: resumoLoading } = useResumoAgendamentos(clienteId);
    const { data: historicoData, isLoading: historicoLoading } = useHistoricoAgendamentos(clienteId, {
        page: currentPage,
        limit: agendamentosPerPage
    });

    // 3. O useEffect de busca foi removido.

    const paginate = (pageNumber) => {
        if (pageNumber !== currentPage) {
            setCurrentPage(pageNumber);
        }
    };
    
    // Extrai os dados com segurança
    const historicoAgendamentos = historicoData?.data || [];
    const totalAgendamentos = historicoData?.totalCount || 0;

    // --- RENDERIZAÇÃO CONDICIONAL ---
    if (resumoLoading && currentPage === 1) {
        return (
            <div className="historico-container">
                <div className="loading-state" style={{ height: '200px' }}>
                    <Loader2 className="animate-spin" /> Carregando histórico...
                </div>
            </div>
        );
    }
    
    if (!resumoLoading && !historicoLoading && historicoAgendamentos.length === 0 && totalAgendamentos === 0) {
        return (
            <div className="historico-container">
                {resumoAgendamentos && <ResumoAgendamentos resumo={resumoAgendamentos} />}
                <div className="placeholder-content" style={{ marginTop: '2rem' }}>
                    <CalendarX2 size={48} className="placeholder-icon"/>
                    <h4>Nenhum Agendamento Encontrado</h4>
                    <p>Este cliente não possui agendamentos no histórico.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="historico-container">
            {resumoAgendamentos && <ResumoAgendamentos resumo={resumoAgendamentos} />}
            
            <div className="historico-list-container">
                {historicoLoading && historicoAgendamentos.length === 0 ? (
                    <div className="loading-state"><Loader2 className="animate-spin" /></div>
                ) : (
                    <ul className="agenda-list">
                        {historicoAgendamentos.map(agendamento => (<AgendamentoCard key={agendamento._id} agendamento={agendamento} />))}
                    </ul>
                )}
            </div>

            {!historicoLoading && totalAgendamentos > agendamentosPerPage && (
                <Pagination 
                    itemsPerPage={agendamentosPerPage} 
                    totalItems={totalAgendamentos} 
                    currentPage={currentPage} 
                    onPageChange={paginate} 
                />
            )}
        </div>
    );
};

export default HistoricoAgendamentos;